import { useCallback, useEffect, useRef, useState } from 'react';

// Per-session, per-module working state (last page, selected run, tab, filters, scroll,
// unsaved edits). Persisted in sessionStorage so switching modules and returning reopens
// the same position; cleared on logout (see App.logout). Stored dates/records untouched.
export function useSessionState(key, initial) {
  const k = `tfs_sess_${key}`;
  const [val, setVal] = useState(() => {
    try {
      const raw = sessionStorage.getItem(k);
      return raw !== null ? JSON.parse(raw) : initial;
    } catch { return initial; }
  });
  useEffect(() => {
    try {
      if (val === undefined || val === null || val === '') sessionStorage.removeItem(k);
      else sessionStorage.setItem(k, JSON.stringify(val));
    } catch { /* ignore quota */ }
  }, [k, val]);
  return [val, setVal];
}

// Save/restore window scroll for a module keyed by a context id (e.g. selected run).
export function useScrollRestore(moduleKey, ctxId, ready) {
  const k = `tfs_sess_scroll_${moduleKey}_${ctxId || 'list'}`;
  const restored = useRef(false);
  useEffect(() => {
    if (!ready) return undefined;
    if (!restored.current) {
      const y = Number(sessionStorage.getItem(k) || 0);
      if (y) requestAnimationFrame(() => window.scrollTo(0, y));
      restored.current = true;
    }
    const onScroll = () => sessionStorage.setItem(k, String(window.scrollY));
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [k, ready]);
}

// Unsaved-draft helpers (cleared on logout via the tfs_draft_ prefix).
export const draftKey = (id) => `tfs_draft_${id}`;
export function loadDraft(id) {
  try { const r = sessionStorage.getItem(draftKey(id)); return r ? JSON.parse(r) : null; } catch { return null; }
}
export function saveDraft(id, data) {
  try { sessionStorage.setItem(draftKey(id), JSON.stringify(data)); } catch { /* ignore */ }
}
export function clearDraft(id) { sessionStorage.removeItem(draftKey(id)); }
